import { useCallback, useMemo } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import type { Permission, PermissionTuple } from '@/types';
import { canDo, isAdmin as checkIsAdmin, isSuperAdmin as checkIsSuperAdmin } from '@/types';

/**
 * Hook for checking user permissions.
 * Works with both tuple-based permissions (from backend) and legacy string permissions.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePermissions() {
  const { user } = useAuthStore();

  // Get tuple-based permissions (preferred)
  const tuplePermissions = useMemo<PermissionTuple[]>(() => {
    if (!user) return [];
    if (user.permissions && user.permissions.length > 0) {
      return user.permissions;
    }
    // Fallback: derive from role
    return getDefaultTuplePermissionsForRole(user.role);
  }, [user]);

  // Legacy string permissions (for backward compatibility)
  const permissions = useMemo<Permission[]>(() => {
    if (!user) return [];
    // Use legacy_permissions from backend if available
    if (user.legacy_permissions && user.legacy_permissions.length > 0) {
      return user.legacy_permissions as Permission[];
    }
    // Convert tuples to legacy strings
    return tuplesToLegacy(tuplePermissions);
  }, [user, tuplePermissions]);

  const isAdmin = useMemo(() => checkIsAdmin(tuplePermissions), [tuplePermissions]);
  const isSuperAdmin = useMemo(() => checkIsSuperAdmin(user), [user]);

  // Check if user can perform resource:action (tuple-based)
  const hasCan = useCallback(
    (resource: string, action: string): boolean => {
      if (!user) return false;
      return canDo(tuplePermissions, resource, action);
    },
    [user, tuplePermissions]
  );

  // Legacy: check if user has a specific string permission
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!user) return false;
      if (isAdmin) return true;
      return permissions.includes(permission);
    },
    [user, permissions, isAdmin]
  );

  // Legacy: check if user has any of the specified permissions
  const hasAnyPermission = useCallback(
    (...perms: Permission[]): boolean => {
      if (!user) return false;
      if (isAdmin) return true;
      return perms.some((p) => permissions.includes(p));
    },
    [user, permissions, isAdmin]
  );

  // Legacy: check if user has all of the specified permissions
  const hasAllPermissions = useCallback(
    (...perms: Permission[]): boolean => {
      if (!user) return false;
      if (isAdmin) return true;
      return perms.every((p) => permissions.includes(p));
    },
    [user, permissions, isAdmin]
  );

  // Convenience methods matching Casdoor RBAC permission matrix
  const can = {
    // Device
    viewDevices: hasCan('device', 'read'),
    editDevices: hasCan('device', 'write'),

    // Command
    viewCommands: hasCan('command', 'read'),
    reboot: hasCan('command', 'reboot'),
    lock: hasCan('command', 'lock'),
    factoryReset: hasCan('command', 'factory_reset'),

    // Firmware
    viewFirmware: hasCan('firmware', 'read'),
    uploadFirmware: hasCan('firmware', 'upload'),

    // Rollout
    viewRollouts: hasCan('rollout', 'read'),
    createRollouts: hasCan('rollout', 'create'),

    // Log
    viewLogs: hasCan('log', 'read'),
    exportLogs: hasCan('log', 'export'),

    // Audit
    viewAudit: hasCan('audit', 'read'),

    // Resource
    viewResources: hasCan('resource', 'read'),
    editResources: hasCan('resource', 'write'),

    // Config
    viewConfigs: hasCan('config', 'read'),
    editConfigs: hasCan('config', 'write'),

    // User
    viewUsers: hasCan('user', 'read'),
    editUsers: hasCan('user', 'write'),
    deleteUsers: hasCan('user', 'delete'),
    manageRoles: hasCan('user', 'manage_roles'),

    // Tenant (super admin only — role check, not permission check)
    manageTenants: isSuperAdmin,
  };

  return {
    permissions,
    tuplePermissions,
    isAdmin,
    isSuperAdmin,
    hasCan,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    can,
  };
}

/**
 * Get default tuple permissions for a role (fallback when backend doesn't send permissions).
 * Matches Casdoor RBAC permission matrix from docs/api/casdoor-rbac-migration.md
 */
function getDefaultTuplePermissionsForRole(role: string): PermissionTuple[] {
  switch (role) {
    case 'mdm-super-admin':
    case 'super_admin':
      return [
        { resource: '*', action: '*' },
        { resource: 'tenant', action: 'manage' },
      ];

    case 'mdm-admin':
    case 'admin':
      return [{ resource: '*', action: '*' }];

    case 'mdm-manager':
    case 'manager':
      return [
        { resource: 'device', action: 'read' },
        { resource: 'device', action: 'write' },
        { resource: 'command', action: 'read' },
        { resource: 'command', action: 'reboot' },
        { resource: 'command', action: 'lock' },
        { resource: 'command', action: 'factory_reset' },
        { resource: 'firmware', action: 'read' },
        { resource: 'rollout', action: 'read' },
        { resource: 'rollout', action: 'create' },
        { resource: 'log', action: 'read' },
        { resource: 'log', action: 'export' },
        { resource: 'audit', action: 'read' },
        { resource: 'config', action: 'read' },
        { resource: 'user', action: 'read' },
      ];

    case 'mdm-support':
    case 'support':
      return [
        { resource: 'device', action: 'read' },
        { resource: 'command', action: 'read' },
        { resource: 'command', action: 'reboot' },
        { resource: 'command', action: 'lock' },
        { resource: 'firmware', action: 'read' },
        { resource: 'rollout', action: 'read' },
        { resource: 'log', action: 'read' },
        { resource: 'log', action: 'export' },
      ];

    default:
      return [];
  }
}

/**
 * Convert tuple permissions to legacy string format for backward compatibility.
 */
function tuplesToLegacy(tuples: PermissionTuple[]): Permission[] {
  const result: Permission[] = [];
  for (const t of tuples) {
    if (t.resource === '*' && t.action === '*') {
      result.push('system:admin' as Permission);
      continue;
    }
    result.push(`${t.resource}:${t.action}` as Permission);
  }
  return result;
}

/**
 * Component wrapper that only renders children if user has permission.
 */
export function RequirePermission({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { hasPermission } = usePermissions();

  if (!hasPermission(permission)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

/**
 * Component wrapper that only renders children if user has any of the permissions.
 */
export function RequireAnyPermission({
  permissions,
  children,
  fallback = null,
}: {
  permissions: Permission[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { hasAnyPermission } = usePermissions();

  if (!hasAnyPermission(...permissions)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

// eslint-disable-next-line react-refresh/only-export-components
export default usePermissions;
